import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Grid, List, Lock, TrendingUp, CheckSquare, Square, X, Star, Clock, Download, FolderPlus, Plus, Flame, FileText, LayoutDashboard, BookTemplate, FolderOpen } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Sidebar } from '../Layout/Sidebar';
import { AppLogo } from '../Layout/AppLogo';
import { FloatingNewButton } from '../Layout/FloatingNewButton';
import { SmartSearchBar } from '../Layout/SmartSearchBar';
import { HeroStatsBlock } from './HeroStatsBlock';
import { PromptCard } from '../Prompts/PromptCard';
import { PromptModal } from '../Prompts/PromptModal';
import { PromptVariationModal } from '../Prompts/PromptVariationModal';
import { PromptEnhanceModal } from '../Prompts/PromptEnhanceModal';
import { SearchResults } from '../Prompts/SearchResults';
import { FolderModal } from '../Folders/FolderModal';
import { MoveToFolderModal } from '../Folders/MoveToFolderModal';
import { FolderGrid } from '../Folders/FolderGrid';
import { FolderBreadcrumb } from '../Folders/FolderBreadcrumb';
import { AddPromptsToFolderModal } from '../Folders/AddPromptsToFolderModal';
import { ContextMenu } from '../Common/ContextMenu';
import { SimpleSidebar } from '../Layout/SimpleSidebar';
import { ToastContainer } from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { usePrompts } from '../../hooks/usePrompts';
import { useFolders } from '../../hooks/useFolders';
import { useStats } from '../../hooks/useStats';
import { Prompt, Folder } from '../../types';
import { flushSync } from 'react-dom';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    prompts,
    allPrompts,
    recentlyExecuted,
    searchResults,
    filters,
    setFilters,
    addPrompt,
    updatePrompt,
    batchUpdatePrompts,
    deletePrompt,
    toggleFavorite,
    getAllPlatforms,
    getAllDomains,
    getAllOutputs,
    getAllWebPlatforms,
    addRecentlyExecutedPrompt,
    movePromptsToFolder,
    isLoading,
  } = usePrompts(user?.id || null);

  const {
    folders,
    isLoading: foldersLoading,
    createFolder,
    updateFolder,
    deleteFolder,
    getFolderPath,
    getSubfolders,
    getRootFolders,
  } = useFolders(user?.id || null);

  const stats = useStats(prompts);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVariationModalOpen, setIsVariationModalOpen] = useState(false);
  const [isEnhanceModalOpen, setIsEnhanceModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [variationPrompt, setVariationPrompt] = useState<Prompt | null>(null);
  const [enhancePrompt, setEnhancePrompt] = useState<Prompt | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Batch categorization state
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState<Set<string>>(new Set());

  // Folder management states
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [parentFolderForNew, setParentFolderForNew] = useState<Folder | null>(null);
  const [isMoveToFolderModalOpen, setIsMoveToFolderModalOpen] = useState(false);
  const [movingSinglePromptId, setMovingSinglePromptId] = useState<string | null>(null);
  const [isAddPromptsModalOpen, setIsAddPromptsModalOpen] = useState(false);
  const [promptDefaultFolder, setPromptDefaultFolder] = useState<string | null>(null);

  // Drag and drop state
  const [draggedPrompt, setDraggedPrompt] = useState<Prompt | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });

  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showToast, dismissToast } = useToast();

  // URL query parameters for filter state
  const [searchParams, setSearchParams] = useSearchParams();

  // Undo deletion state
  const [pendingDeletions, setPendingDeletions] = useState<Map<string, {
    prompt: Prompt;
    timeoutId: NodeJS.Timeout;
  }>>(new Map());

  // Sync URL query parameters with filter state
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    
    if (filterParam === 'favorites') {
      setFilters(prev => ({ ...prev, showFavoritesOnly: true, showTemplatesOnly: false, showRecentlyExecutedOnly: false }));
    } else if (filterParam === 'recent') {
      setFilters(prev => ({ ...prev, showRecentlyExecutedOnly: true, showFavoritesOnly: false, showTemplatesOnly: false }));
    } else if (filterParam === 'templates') {
      setFilters(prev => ({ ...prev, showTemplatesOnly: true, showFavoritesOnly: false, showRecentlyExecutedOnly: false }));
    } else if (!filterParam) {
      // Clear all view filters when no filter param is present
      setFilters(prev => ({ ...prev, showFavoritesOnly: false, showTemplatesOnly: false, showRecentlyExecutedOnly: false }));
    }
  }, [searchParams]);

  // Save sidebar state to localStorage
  useEffect(() => {
    const saved = localStorage.getItem('promptos_sidebar_collapsed');
    if (saved !== null) {
      setIsSidebarCollapsed(saved === 'true');
    }
  }, []); // Only run on mount, not on every state change

  useEffect(() => {
    localStorage.setItem('promptos_sidebar_collapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  const handleToggleSidebar = () => {
    console.log('🔘 Dashboard: Toggling mobile menu from', isMobileMenuOpen, 'to', !isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDesktopSidebarToggle = () => {
    console.log('🔘 Dashboard: Toggling desktop sidebar from', isSidebarCollapsed, 'to', !isSidebarCollapsed);
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Cleanup pending deletions on unmount
  useEffect(() => {
    return () => {
      pendingDeletions.forEach(({ timeoutId }) => {
        clearTimeout(timeoutId);
      });
    };
  }, [pendingDeletions]);

  // Clear batch mode when changing views or filters
  useEffect(() => {
    setIsBatchMode(false);
    setSelectedPrompts(new Set());
  }, [viewMode, filters.search, filters.showTemplatesOnly, filters.showFavoritesOnly, filters.showRecentlyExecutedOnly]);

  const handleNewPrompt = () => {
    setEditingPrompt(null);
    // Set the current folder as the default for new prompts
    setPromptDefaultFolder(filters.folderId || null);
    setIsModalOpen(true);
  };

  const handleEditPrompt = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setIsModalOpen(true);
  };

  const handleGenerateVariations = (prompt: Prompt) => {
    setVariationPrompt(prompt);
    setIsVariationModalOpen(true);
  };

  const handleEnhancePrompt = (prompt: Prompt) => {
    setEnhancePrompt(prompt);
    setIsEnhanceModalOpen(true);
  };

  const handleSavePrompt = (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    // Ensure new prompts are saved to the current folder
    const promptWithFolder = {
      ...promptData,
      folderId: promptDefaultFolder
    };
    addPrompt(promptWithFolder);
    setIsModalOpen(false);
  };

  const handleSaveVariation = (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    addPrompt(promptData);
    setIsVariationModalOpen(false);
  };

  const handleSaveEnhanced = (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    addPrompt(promptData);
    setIsEnhanceModalOpen(false);
  };

  const handleUpdatePrompt = (id: string, updates: Partial<Prompt>, historyType?: 'enhanced' | 'manual' | 'variation') => {
    updatePrompt(id, updates, historyType);
    setIsModalOpen(false);
  };

  const handleUpdateOriginal = (id: string, updates: Partial<Prompt>) => {
    updatePrompt(id, updates, 'enhanced');
    setIsEnhanceModalOpen(false);
  };

  const handleDeletePrompt = async (id: string) => {
    const promptToDelete = prompts.find(p => p.id === id);
    if (!promptToDelete) return;

    // Create a copy of the prompt to restore if needed
    const promptCopy = { ...promptToDelete };

    // Set up delayed deletion with undo option
    const timeoutId = setTimeout(async () => {
      // Actually delete from database after 5 seconds
      const success = await deletePrompt(id);
      
      // Remove from pending deletions regardless of success/failure
      setPendingDeletions(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    }, 5000);

    // Store the pending deletion with the prompt copy and timeout
    setPendingDeletions(prev => {
      const newMap = new Map(prev);
      newMap.set(id, { 
        prompt: promptCopy, 
        timeoutId 
      });
      return newMap;
    });

    // Show undo toast
    showToast('Prompt deleted', {
      type: 'info',
      action: {
        label: 'Undo',
        onClick: () => handleUndoDelete(id),
      },
      duration: 4500, // Slightly less than the 5s timeout
    });
  };

  const handleUndoDelete = (id: string) => {
    const pendingDeletion = pendingDeletions.get(id);
    if (!pendingDeletion) return;

    // Clear the pending deletion timeout immediately
    clearTimeout(pendingDeletion.timeoutId);

    // Remove from pending deletions - simple state update
    setPendingDeletions(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });

    // Show success message
    showToast('Prompt restored', {
      type: 'success',
      duration: 2000,
    });
  };

  const handleSearchChange = (query: string) => {
    setFilters({ ...filters, search: query });
  };

  // Handle prompt execution tracking
  const handlePromptCopy = (prompt: Prompt) => {
    addRecentlyExecutedPrompt(prompt);
  };

  // Batch categorization handlers
  const handleToggleBatchMode = () => {
    setIsBatchMode(!isBatchMode);
    setSelectedPrompts(new Set());
  };

  const handleTogglePromptSelection = (promptId: string) => {
    setSelectedPrompts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  const handleSelectAllPrompts = () => {
    const currentPrompts = isSearchMode ? searchResults.map(r => r.prompt) : prompts;
    const allIds = new Set(currentPrompts.map(p => p.id));
    setSelectedPrompts(allIds);
  };

  const handleDeselectAllPrompts = () => {
    setSelectedPrompts(new Set());
  };

  // Drag and drop handlers
  const handleDragStart = (prompt: Prompt) => {
    setDraggedPrompt(prompt);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setDraggedPrompt(null);
    setIsDragging(false);
  };

  const handleDropOnFolder = async (folderId: string, promptId: string) => {
    try {
      await movePromptsToFolder([promptId], folderId);
      
      // Show success feedback
      const prompt = prompts.find(p => p.id === promptId);
      const folder = folders.find(f => f.id === folderId);
      
      if (prompt && folder) {
        // You could add a toast notification here
        console.log(`Moved "${prompt.title}" to "${folder.name}"`);
      }
    } catch (error) {
      console.error('Error moving prompt to folder:', error);
      // You could add error handling here
    }
  };
  const handleExportPrompts = () => {
    if (prompts.length === 0) {
      alert('No prompts to export.');
      return;
    }

    const exportData = {
      exported_at: new Date().toISOString(),
      total_prompts: prompts.length,
      prompts: prompts.map(prompt => ({
        id: prompt.id,
        title: prompt.title,
        body: prompt.body,
        aiPlatform: prompt.aiPlatform,
        domain: prompt.domain,
        output: prompt.output,
        platforms: prompt.platforms,
        webPlatforms: prompt.webPlatforms,
        isFavorite: prompt.isFavorite,
        isTemplate: prompt.isTemplate,
        createdAt: prompt.createdAt.toISOString(),
        updatedAt: prompt.updatedAt.toISOString(),
        lastExecutedAt: prompt.lastExecutedAt?.toISOString(),
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `prometheus-prompts-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Folder management handlers
  const handleNewFolder = (parentFolder?: Folder) => {
    setEditingFolder(null);
    setParentFolderForNew(parentFolder || null);
    setIsFolderModalOpen(true);
  };

  const handleEditFolder = (folder: Folder) => {
    setEditingFolder(folder);
    setParentFolderForNew(null);
    setIsFolderModalOpen(true);
  };

  const handleSaveFolder = async (folderData: { name: string; parentId?: string; color?: string }) => {
    console.log('🔵 Dashboard: handleSaveFolder called', folderData);
    
    try {
      const result = await createFolder(folderData);
      console.log('✅ Dashboard: Folder created successfully', result);
      
      // Close modal only after successful creation
      setIsFolderModalOpen(false);
      setEditingFolder(null);
      setParentFolderForNew(null);
      
    } catch (error) {
      console.error('❌ Dashboard: Error in handleSaveFolder:', error);
      // Don't re-throw, let the modal handle it internally
    }
  };

  const handleUpdateFolder = async (id: string, updates: Partial<Folder>) => {
    await updateFolder(id, updates);
    setIsFolderModalOpen(false);
    setEditingFolder(null);
  };

  const handleDeleteFolder = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this folder? All prompts in this folder will be moved to the root level.')) {
      await deleteFolder(id);
    }
  };

  const handleFolderClick = (folderId: string) => {
    // Clear URL query parameters when navigating to a folder
    setSearchParams({});
    // Clear all view filters and navigate to folder's default view
    setFilters({
      ...filters,
      folderId,
      showFavoritesOnly: false,
      showTemplatesOnly: false,
      showRecentlyExecutedOnly: false,
    });
  };

  const handleNavigateToFolder = (folderId?: string) => {
    // Clear URL query parameters when using breadcrumb navigation
    setSearchParams({});
    // Clear all view filters and navigate to folder's default view
    setFilters({
      ...filters,
      folderId,
      showFavoritesOnly: false,
      showTemplatesOnly: false,
      showRecentlyExecutedOnly: false,
    });
  };

  const handleMovePrompts = async (folderId: string | null) => {
    const promptIds = movingSinglePromptId 
      ? [movingSinglePromptId] 
      : Array.from(selectedPrompts);
    await movePromptsToFolder(promptIds, folderId);
    
    if (movingSinglePromptId) {
      setMovingSinglePromptId(null);
    } else {
      setSelectedPrompts(new Set());
    }
    setIsMoveToFolderModalOpen(false);
  };

  const handleMoveSinglePrompt = (promptId: string) => {
    setMovingSinglePromptId(promptId);
    setIsMoveToFolderModalOpen(true);
  };

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('🖱️ Right-click detected at:', e.clientX, e.clientY, 'Target:', e.target);
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
    });
  };

  const handleCloseContextMenu = () => {
    console.log('🖱️ Closing context menu');
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  const handleNavigateHome = () => {
    // Clear all filters to return to main dashboard view
    setFilters({
      search: '',
      showTemplatesOnly: false,
      showFavoritesOnly: false,
      showRecentlyExecutedOnly: false,
      sortBy: 'recent',
      folderId: undefined, // This clears folder view
    });
  };

  // Helper function for view mode button styling
  const getViewModeButtonClasses = (isActive: boolean) => {
    return `p-2 rounded-lg transition-all duration-300 ${
      isActive
        ? 'bg-gray-200/80 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 border border-gray-300/60 dark:border-gray-600/60'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100/60 dark:hover:bg-gray-800/30 border border-transparent'
    }`;
  };
  const handleContextMenuNewFolder = () => {
    console.log('🖱️ Creating new folder from context menu');
    handleNewFolder(currentFolder || undefined);
    handleCloseContextMenu();
  };

  const getDisplayTitle = () => {
    if (filters.search) {
      return `Search results for "${filters.search}"`;
    }
    
    if (filters.showTemplatesOnly) {
      return 'Template Collection';
    }

    if (filters.showFavoritesOnly) {
      return 'Favorite Prompts';
    }

    if (filters.showRecentlyExecutedOnly) {
      return 'Recently Executed';
    }

    if (filters.folderId) {
      const currentFolder = folders.find(f => f.id === filters.folderId);
      return currentFolder ? `Folder: ${currentFolder.name}` : 'Folder';
    }
    
    return 'All Prompts';
  };

  const getDisplayIcon = () => {
    if (filters.showFavoritesOnly) {
      return <Star className="h-6 w-6 text-yellow-500 dark:text-yellow-400 fill-current" />;
    }
    
    if (filters.showRecentlyExecutedOnly) {
      return <Clock className="h-6 w-6 text-blue-500 dark:text-blue-400" />;
    }
    
    return <Lock className="h-6 w-6 text-modular-frosted-indigo dark:text-modular-frosted-indigo" />;
  };

  const isSearchMode = filters.search.trim().length > 0;
  const showStats = !isSearchMode && !filters.showTemplatesOnly && !filters.showFavoritesOnly && !filters.showRecentlyExecutedOnly && !filters.aiPlatform && !filters.folderId;

  // Get current prompts for display
  const currentPrompts = isSearchMode ? searchResults.map(r => r.prompt) : prompts;
  const hasPrompts = currentPrompts.length > 0;

  // Filter out prompts that are pending deletion
  const visiblePrompts = currentPrompts.filter(prompt => !pendingDeletions.has(prompt.id));
  const hasVisiblePrompts = visiblePrompts.length > 0;

  // Show batch categorize button when we have prompts
  const showBatchCategorizeButton = hasVisiblePrompts && (viewMode === 'grid' || viewMode === 'list');

  // Get current folder and subfolders
  const currentFolder = filters.folderId ? folders.find(f => f.id === filters.folderId) : null;
  const currentSubfolders = getSubfolders(filters.folderId);
  const folderPath = filters.folderId ? getFolderPath(filters.folderId) : [];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-canvas-fog via-modular-ivory-mist to-modular-lunar-sage 
      dark:bg-dark-gradient transition-all duration-500">
      
      {/* Floating Search Bar */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
          <div className="p-2">
            <SmartSearchBar
              searchQuery={filters.search}
              onSearchChange={handleSearchChange}
              prompts={allPrompts}
              recentlyExecuted={recentlyExecuted}
            />
          </div>
        </div>
      </div>

      <div className="flex">
        <SimpleSidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleDesktopSidebarToggle}
          className="hidden lg:block"
        />

        {/* Mobile sidebar overlay */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Mobile sidebar */}
        <div className={`lg:hidden fixed left-0 top-0 h-full w-72 bg-white/98 dark:bg-dark-surface/98 
          backdrop-blur-xl border-r border-gray-200/60 dark:border-dark-border/40 z-50 shadow-xl
          transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
          
          {/* Mobile sidebar content */}
          <div className="px-6 py-6 border-b border-gray-200/60 dark:border-dark-border/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AppLogo size="sm" animated={false} />
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text">
                    PromptOS
                  </h1>
                  <p className="text-sm text-modular-accent-blue dark:text-modular-accent-blue font-medium -mt-1">
                    AI Prompt OS
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <nav className="space-y-2">
              {[
                { label: 'Dashboard', icon: Flame, path: '/dashboard' },
                { label: 'All Prompts', icon: FileText, path: '/dashboard' },
                { label: 'Favorites', icon: Star, path: '/dashboard?filter=favorites' },
                { label: 'Recent', icon: Clock, path: '/dashboard?filter=recent' },
                { label: 'Templates', icon: FolderOpen, path: '/listing-templates' },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path === '/dashboard' && location.pathname === '/dashboard' && !location.search);
                
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false); // Close sidebar after navigation
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-semibold rounded-xl 
                      transition-all duration-300 text-left ${
                      isActive
                        ? 'bg-modular-primary-gradient text-white shadow-modular-glow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-dark-hover hover:text-modular-accent-blue dark:hover:text-modular-accent-blue'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-4 flex-shrink-0" />
                    <span className="font-semibold">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <main className={`flex-1 px-4 py-6 lg:px-6 transition-all duration-300 ease-in-out pt-20`}
          style={{ minHeight: '100vh' }}
          onContextMenu={handleContextMenu}
        >
          {/* Mobile Sidebar Toggle Button */}
          <button
            onClick={handleToggleSidebar}
            className="lg:hidden fixed top-4 left-4 z-50 flex items-center justify-center p-2.5 bg-white/95 dark:bg-dark-surface/95 
              backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 
              text-gray-600 dark:text-gray-300 hover:bg-modular-accent-blue hover:text-white 
              dark:hover:bg-modular-accent-blue rounded-lg transition-all duration-300 
              shadow-sm hover:shadow-md min-w-[44px] min-h-[44px] group"
            title={isSidebarCollapsed ? 'Open menu' : 'Close menu'}
          >
            <Menu className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
          </button>

          <div 
            className="w-full"
            onContextMenu={handleContextMenu}
          >
            {/* Show Hero Stats Block when viewing all prompts */}
            {showStats && (
              <HeroStatsBlock
                totalPrompts={stats.totalPrompts}
                favoritePrompts={stats.favoritePrompts}
                templatePrompts={stats.templatePrompts}
                mostUsedPlatform={stats.mostUsedPlatform}
                recentActivity={stats.recentActivity}
                totalTags={0}
                creationStreak={stats.creationStreak}
              />
            )}

            {/* Folder Breadcrumb */}
            {filters.folderId && (
              <FolderBreadcrumb
                folderPath={folderPath}
                onNavigateToFolder={handleNavigateToFolder}
              />
            )}

            {/* Drag and drop overlay */}
            {isDragging && (
              <div className="fixed inset-0 bg-fire-500/5 dark:bg-fire-400/5 z-40 pointer-events-none 
                flex items-center justify-center transition-all duration-300">
                <div className="bg-white dark:bg-dark-surface border-2 border-dashed border-fire-500 
                  dark:border-fire-400 rounded-xl p-6 shadow-fire-glow animate-pulse">
                  <p className="text-fire-600 dark:text-fire-400 font-medium text-center">
                    🗂️ Drop on a folder to organize your prompt
                  </p>
                </div>
              </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 lg:space-x-3">
                  {getDisplayIcon()}
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-dark-text">
                    {getDisplayTitle()}
                  </h2>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="hidden sm:flex items-center text-sm text-modular-frosted-indigo dark:text-modular-frosted-indigo 
                    bg-gray-100/80 dark:bg-gray-800/60 px-3 py-1.5 rounded-full 
                    border border-gray-200/60 dark:border-gray-700/60">
                    <TrendingUp className="h-4 w-4 mr-1.5" />
                    <span className="font-medium">
                      {isSearchMode ? searchResults.length : prompts.length} prompt{(isSearchMode ? searchResults.length : prompts.length) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-1 lg:space-x-3">
                {/* New Folder Button */}
                <button
                  onClick={() => handleNewFolder(currentFolder || undefined)}
                  className="hidden sm:flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300 
                    hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 
                    dark:hover:bg-gray-700/50 rounded-lg transition-all duration-300 
                    border border-gray-200/60 dark:border-gray-700/50 hover:border-gray-300/80 dark:hover:border-gray-600/80"
                  title="Create new folder"
                >
                  <FolderPlus className="h-4 w-4 mr-2" />
                  New Folder
                </button>

                {/* Add Prompts Button - Only show when in a folder */}
                {filters.folderId && (
                  <button
                    onClick={() => setIsAddPromptsModalOpen(true)} // Keep this action
                    className="hidden sm:flex items-center px-3 py-2 text-sm text-blue-600 dark:text-blue-400 
                      hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50/80 
                      dark:hover:bg-blue-900/20 rounded-lg transition-all duration-300 
                      border border-blue-200/60 dark:border-blue-700/50 hover:border-blue-300/80 dark:hover:border-blue-600/80"
                    title="Add existing prompts to this folder"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Prompts
                  </button>
                )}

                {/* Export Control */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleExportPrompts}
                    disabled={prompts.length === 0}
                    className="hidden lg:flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300
                      hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 
                      dark:hover:bg-gray-700/50 rounded-lg transition-all duration-300 
                      border border-gray-200/60 dark:border-gray-700/50 hover:border-gray-300/80 dark:hover:border-gray-600/80 disabled:opacity-50 
                      disabled:cursor-not-allowed"
                    title="Export all prompts as JSON"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                </div>

                {/* Batch categorization controls */}
                {showBatchCategorizeButton && (
                  <div className="hidden md:flex items-center space-x-2">
                    {!isBatchMode ? (
                      <button
                        onClick={handleToggleBatchMode}
                        className="flex items-center px-3 py-2 text-sm text-blue-600 dark:text-blue-400 
                          hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50/80 
                          dark:hover:bg-blue-900/20 rounded-lg transition-all duration-300 
                          border border-blue-200/60 dark:border-blue-700/50 hover:border-blue-300/80 dark:hover:border-blue-600/80"
                      >
                        <CheckSquare className="h-4 w-4 mr-2" />
                        Batch Select
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2 bg-blue-50/80 dark:bg-blue-900/20 
                        border border-blue-200/60 dark:border-blue-700/50 rounded-lg p-2">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          {selectedPrompts.size} selected
                        </span>
                        
                        {selectedPrompts.size < currentPrompts.length && (
                          <button
                            onClick={handleSelectAllPrompts}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 
                              dark:hover:text-blue-300 underline"
                          >
                            Select All
                          </button>
                        )}
                        
                        {selectedPrompts.size > 0 && (
                          <>
                            <button
                              onClick={handleDeselectAllPrompts}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 
                                dark:hover:text-blue-300 underline"
                            >
                              Deselect All
                            </button>
                            
                            <button
                              onClick={() => setIsMoveToFolderModalOpen(true)}
                              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                            >
                              Move to Folder
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={handleToggleBatchMode}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 
                            dark:hover:text-gray-300 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={getViewModeButtonClasses(viewMode === 'grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={getViewModeButtonClasses(viewMode === 'list')}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Folders Grid */}
            {(() => {
              const rootFolders = getRootFolders();
              const subfoldersToShow = filters.folderId ? currentSubfolders : rootFolders;
              
              console.log('🔵 Dashboard: Folder display logic', {
                totalFolders: folders.length,
                rootFolders: rootFolders.length,
                currentSubfolders: currentSubfolders.length,
                folderId: filters.folderId,
                subfoldersToShow: subfoldersToShow.length,
                shouldShow: subfoldersToShow.length > 0
              });
              
              return subfoldersToShow.length > 0;
            })() && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                    {filters.folderId ? 'Subfolders' : 'Folders'} ({(filters.folderId ? currentSubfolders : getRootFolders()).length})
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Total: {folders.length} folders
                  </div>
                </div>
                <FolderGrid
                  folders={filters.folderId ? currentSubfolders : getRootFolders()}
                  onFolderClick={handleFolderClick}
                  onEditFolder={handleEditFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onCreateSubfolder={handleNewFolder}
                  onDropPrompt={handleDropOnFolder}
                  draggedPromptId={draggedPrompt?.id || null}
                />
              </div>
            )}

            {/* Content Area */}
            {isLoading || foldersLoading ? (
              <div 
                className="flex items-center justify-center py-16"
                onContextMenu={handleContextMenu}
              >
                <div className="text-center">
                  <div className="relative mb-6"> 
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-modular-stone-alloy 
                      border-t-modular-frosted-indigo mx-auto"></div>
                    <Flame className="absolute inset-0 h-6 w-6 text-modular-frosted-indigo m-auto animate-pulse" />
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-dark-text-secondary font-medium">
                      Loading your library...
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Retrieving your creative arsenal
                    </p>
                    <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                        {isSearchMode ? searchResults.filter(r => !pendingDeletions?.has(r.prompt.id)).length : visiblePrompts.length} prompt{(isSearchMode ? searchResults.filter(r => !pendingDeletions?.has(r.prompt.id)).length : visiblePrompts.length) !== 1 ? 's' : ''}
                      <p>Folders: {foldersLoading ? 'Loading...' : folders.length}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : isSearchMode ? (
              <SearchResults
                results={searchResults}
                onEdit={handleEditPrompt}
                onDelete={handleDeletePrompt}
                onToggleFavorite={toggleFavorite}
                onGenerateVariations={handleGenerateVariations}
                onEnhancePrompt={handleEnhancePrompt}
                onUpdatePrompt={handleUpdatePrompt}
                onPromptCopy={handlePromptCopy}
                isBatchMode={isBatchMode}
                selectedPrompts={selectedPrompts}
                onToggleSelection={handleTogglePromptSelection}
                onMoveToFolder={handleMoveSinglePrompt}
              />
            ) : visiblePrompts.length === 0 ? (
              <div 
                className="text-center py-16"
                onContextMenu={handleContextMenu}
              >
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 opacity-20 rounded-full blur-3xl"></div>
                  <div className="relative bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-800/80 dark:to-gray-900/60 rounded-full p-8 shadow-lg 
                    border border-gray-200/60 dark:border-gray-700/50 mx-auto w-32 h-32 
                    flex items-center justify-center"> 
                    <Lock className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-4">
                  {filters.showFavoritesOnly 
                    ? "No favorite prompts yet" 
                    : filters.showRecentlyExecutedOnly 
                    ? "No recently executed prompts" 
                    : filters.aiPlatform 
                    ? `No prompts for ${filters.aiPlatform}` 
                    : "Your PromptOS library awaits"}
                </h3>
                
                <p className="text-gray-600 dark:text-dark-text-secondary mb-8 max-w-md mx-auto">
                  {filters.showFavoritesOnly 
                    ? "Star your best prompts to see them here" 
                    : filters.showRecentlyExecutedOnly 
                    ? "Copy prompts to track your recent activity" 
                    : filters.aiPlatform 
                    ? `Create prompts specifically for ${filters.aiPlatform}` 
                    : "Start building your collection of AI prompts and unlock your creative potential"}
                </p>
                
                {/* Show create button when library is empty */}
                {!filters.showFavoritesOnly && !filters.showRecentlyExecutedOnly && !filters.aiPlatform && (
                  <div className="flex items-center justify-center">
                    <button 
                      onClick={handleNewPrompt}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg hover:shadow-lg 
                        transition-all duration-300 shadow-md transform hover:scale-105 
                        active:scale-95 flex items-center"
                    >
                      <Lock className="h-5 w-5 mr-2" />
                      Create First Prompt
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div 
                className={`
                ${viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
                  : 'space-y-4'
                }
              `}
                onContextMenu={handleContextMenu}
              >
                {visiblePrompts.map((prompt) => (
                  <PromptCard
                    key={prompt.id}
                    prompt={prompt}
                    onEdit={handleEditPrompt}
                    onDelete={handleDeletePrompt}
                    onToggleFavorite={toggleFavorite}
                    onGenerateVariations={handleGenerateVariations}
                    onEnhancePrompt={handleEnhancePrompt}
                    onUpdatePrompt={handleUpdatePrompt}
                    onPromptCopy={handlePromptCopy}
                    isBatchMode={isBatchMode}
                    isSelected={selectedPrompts.has(prompt.id)}
                    onToggleSelection={handleTogglePromptSelection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedPrompt?.id === prompt.id}
                    onMoveToFolder={handleMoveSinglePrompt}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Floating New Prompt Button */}
      <FloatingNewButton onClick={handleNewPrompt} />

      <PromptModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePrompt}
        onUpdate={handleUpdatePrompt}
        prompt={editingPrompt}
        defaultFolderId={promptDefaultFolder}
      />

      {variationPrompt && (
        <PromptVariationModal
          isOpen={isVariationModalOpen}
          onClose={() => setIsVariationModalOpen(false)}
          originalPrompt={variationPrompt}
          onSaveVariation={handleSaveVariation}
        />
      )}

      {enhancePrompt && (
        <PromptEnhanceModal
          isOpen={isEnhanceModalOpen}
          onClose={() => setIsEnhanceModalOpen(false)}
          originalPrompt={enhancePrompt}
          onSaveEnhanced={handleSaveEnhanced}
          onUpdateOriginal={handleUpdateOriginal}
        />
      )}

      {/* Folder Management Modals */}
      <FolderModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSave={handleSaveFolder}
        onUpdate={handleUpdateFolder}
        folder={editingFolder}
        parentFolder={parentFolderForNew}
      />

      <MoveToFolderModal
        isOpen={isMoveToFolderModalOpen}
        onClose={() => {
          setIsMoveToFolderModalOpen(false);
          setMovingSinglePromptId(null);
        }}
        folders={folders}
        selectedPromptIds={movingSinglePromptId ? [movingSinglePromptId] : Array.from(selectedPrompts)}
        onMovePrompts={handleMovePrompts}
      />

      {/* Add Prompts to Folder Modal */}
      {filters.folderId && (
        <AddPromptsToFolderModal
          isOpen={isAddPromptsModalOpen}
          onClose={() => setIsAddPromptsModalOpen(false)}
          targetFolderId={filters.folderId}
          onMovePrompts={movePromptsToFolder}
        />
      )}
    </div>
  );
};